package io.horion.cv.api.repository.cv

import io.horion.cv.api.config.HttpClient
import io.horion.cv.api.datasource.Endpoint
import io.horion.cv.api.datasource.common.Generate
import io.horion.cv.api.datasource.cv.Model
import io.horion.cv.api.datasource.cv.OrionCV
import io.horion.cv.api.datasource.response.Response
import io.horion.cv.api.utils.Constants.CV_API_URL
import io.ktor.client.call.body
import io.ktor.client.request.accept
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.put
import io.ktor.client.request.headers
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType

class CvRepositoryImpl: CvRepository {

    override suspend fun createCv(token: String,model: Model, cv: OrionCV): Response? {
        return try {
            val response : HttpResponse = HttpClient.client.post("$CV_API_URL${Endpoint.Cv.path}") {
                contentType(ContentType.Application.Json)
                url {
                    parameter("model", model.name)
                }
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                accept(ContentType.Application.Json)
                setBody(cv)
            }

            return when (response.status) {
                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> {
                    response.body<Response>()
                }
                HttpStatusCode.Unauthorized -> {
                    null
                }
                else ->
                    Response(
                        success = false,
                        message = "Erreur serveur (${response.status})"
                    )
            }
        } catch (e: Exception) {
            Response(
                success = false,
                message = e.message ?: "Erreur réseau"
            )
        }
    }

    override suspend fun checkCvById(token: String, id: String): OrionCV? {
        return try {
            val response: HttpResponse = HttpClient.client.get("$CV_API_URL${Endpoint.FindCvById.path}") {
                contentType(ContentType.Application.Json)
                url { parameter("id", id) }
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                accept(ContentType.Application.Json)
            }

            when (response.status) {
                HttpStatusCode.OK -> response.body<OrionCV>()
                HttpStatusCode.Unauthorized -> null
                else -> null
            }
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun updateCv(token: String, cv: OrionCV, model: String?): Response? {
        return try {
            val response: HttpResponse = HttpClient.client.put("$CV_API_URL${Endpoint.Update.path}") {
                contentType(ContentType.Application.Json)
                accept(ContentType.Application.Json)
                if (model != null) {
                    url { parameter("model", model) }
                }
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                setBody(cv)
            }

            when (response.status) {
                HttpStatusCode.OK -> response.body<Response>()
                HttpStatusCode.Unauthorized -> null
                else -> Response(false, "Erreur serveur (${response.status})")
            }
        } catch (e: Exception) {
            Response(false, e.message ?: "Erreur réseau")
        }
    }

    override suspend fun checkCvList(token: String): List<OrionCV>? {
        return try {
            val response: HttpResponse = HttpClient.client.get("$CV_API_URL${Endpoint.Check.path}") {
                contentType(ContentType.Application.Json)
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                accept(ContentType.Application.Json)
            }

            when (response.status) {
                HttpStatusCode.OK -> response.body<List<OrionCV>>()
                HttpStatusCode.Unauthorized -> null
                else -> emptyList()
            }
        } catch (e: Exception) {
            emptyList()
        }
    }

    override suspend fun checkCvByName(token: String, name: String): OrionCV? {
        return try {
            val response: HttpResponse = HttpClient.client.get("$CV_API_URL${Endpoint.FindCv.path}") {
                contentType(ContentType.Application.Json)
                url { parameter("name", name) }
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                accept(ContentType.Application.Json)
            }

            when (response.status) {
                HttpStatusCode.OK -> response.body<OrionCV>()
                HttpStatusCode.Unauthorized -> null
                else -> null
            }
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun saveCv(token: String,cv: OrionCV): Response? {
        return try {
            val response: HttpResponse = HttpClient.client.post("$CV_API_URL${Endpoint.Save.path}") {
                contentType(ContentType.Application.Json)
                accept(ContentType.Application.Json)
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                setBody(cv)
            }

            return when (response.status) {
                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> {
                    response.body<Response>()
                }
                HttpStatusCode.Unauthorized -> {
                    null
                }
                else ->
                    Response(
                        success = false,
                        message = "Erreur serveur (${response.status})"
                    )
            }
        } catch (e: Exception) {
            Response(
                success = false,
                message = e.message ?: "Erreur réseau"
            )
        }
    }

    override suspend fun uploadImage(bytes: ByteArray, token: String): Response? {
        return try {

            val response: HttpResponse = HttpClient.client.post("$CV_API_URL${Endpoint.UploadImage.path}") {

                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }

                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append(
                                key = "file",
                                value = bytes,
                                headers = Headers.build {
                                    append(HttpHeaders.ContentDisposition, "filename=image.jpg")
                                    append(HttpHeaders.ContentType, "image/jpeg")
                                }
                            )
                        }
                    )
                )
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    val fileName = response.bodyAsText()
                    Response(true, fileName)
                }

                HttpStatusCode.BadRequest -> {
                    Response(false, response.bodyAsText())
                }

                else -> {
                    Response(false, "Erreur serveur (${response.status})")
                }
            }

        } catch (e: Exception) {
            Response(false, e.message ?: "Erreur réseau")
        }
    }

    override suspend fun deleteCv(token: String, cvId: String): Response? {
        return try {
            val response: HttpResponse = HttpClient.client.delete("$CV_API_URL${Endpoint.DeleteCv.path}") {
                contentType(ContentType.Application.Json)
                url { parameter("id", cvId) }
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                accept(ContentType.Application.Json)
            }

            when (response.status) {
                HttpStatusCode.OK -> response.body<Response>()
                HttpStatusCode.Unauthorized -> null
                else -> Response(false, "Erreur serveur (${response.status})")
            }
        } catch (e: Exception) {
            Response(false, e.message ?: "Erreur réseau")
        }
    }

    override suspend fun checkHistory(token: String, refresh: Boolean): List<Generate>? {
        return try {
            val response : HttpResponse = HttpClient.client.get("$CV_API_URL${Endpoint.History.path}") {
                contentType(ContentType.Application.Json)
                if (refresh) url { parameter("refresh", "true") }
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                accept(ContentType.Application.Json)
            }

            return when (response.status) {
                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> {
                    response.body<List<Generate>>()
                }
                HttpStatusCode.Unauthorized -> {
                    null
                }
                else ->
                    emptyList()
            }

        } catch (e: Exception) {
            emptyList()
        }
    }
}
